/**
 * TL-054: runtime colour tokens. Turns a tenant's primary and accent colour
 * into the app's existing CSS variables.
 *
 * How it works
 *  - The defaults in `styles/app.css` are DrTutor's palette and never change.
 *    A tenant on those defaults (or with no colours set) gets no override at
 *    all, so DrTutor looks exactly as before.
 *  - Otherwise one `<style id="tenant-brand-tokens">` is appended to `<head>`
 *    with a light block and a `[data-theme="dark"]` block. A style element
 *    rather than an inline style on `<html>` because dark mode needs its own
 *    values and an inline style would beat the dark selector.
 *  - Brand tokens (teal ramp, links, buttons, focus) come from the primary:
 *    the ink shade is darkened until white text on it passes 5:1, the dark
 *    link shade is lightened until it passes 7:1 on the dark canvas.
 *  - The mint-grey neutrals, borders, shadows and dark surfaces are the
 *    shipped values with their hue rotated to the tenant's hue (saturation
 *    scaled to match), read live from the stylesheet so nothing is
 *    duplicated here. Text tokens are re-checked so they never read worse
 *    than the shipped contrast.
 *  - Sand (tag chips, gold ink) comes from the accent colour.
 */

import {
  contrast,
  ensureContrast,
  hslCss,
  hslToRgb,
  isHexColour,
  parseColour,
  rgbToHsl,
  toCss,
  withAlpha,
  WHITE,
  type Hsl,
  type Rgba,
} from "./colour";

const STYLE_ID = "tenant-brand-tokens";

/** DrTutor's shipped primaries and accents (current and retired). No override. */
const DEFAULT_PRIMARIES = new Set(["#0fa5a5", "#11706b", "#1a8580", "#0d8a8a"]);
const DEFAULT_ACCENTS = new Set(["#f5a623", "#c8a56a"]);

/** Tokens whose every colour is hue-rotated to the tenant. Both themes. */
const RETINT_TOKENS = [
  "--color-brand-navy", "--color-brand-navy-deep",
  "--color-warm-cream-100", "--color-warm-cream-200", "--color-warm-cream-300", "--color-warm-cream-400",
  "--color-warm-cream-500", "--color-warm-cream-600", "--color-warm-cream-700", "--color-warm-cream-800",
  "--color-warm-cream-900", "--color-warm-cream-950",
  "--color-surface-teal", "--color-surface-dark", "--color-sidebar", "--color-input", "--color-input-focus",
  "--color-track", "--color-thumb", "--color-page", "--color-surface", "--color-surface-alt", "--color-surface-raised",
  "--color-primary", "--color-secondary", "--color-muted", "--color-faint",
  "--color-border-subtle", "--color-border-default", "--color-border-emphasis", "--color-border-strong",
  "--color-overlay",
  "--color-command-bar-bg", "--color-command-bar-text", "--color-command-bar-muted", "--color-command-bar-icon",
  "--color-command-bar-icon-hover", "--color-command-bar-divider", "--color-command-bar-hover-surface",
  "--color-tooltip-bg", "--color-tooltip-fg", "--color-tooltip-border", "--shadow-tooltip",
  "--color-info-soft", "--color-accent-soft",
  "--shadow-xs", "--shadow-sm", "--shadow-md", "--shadow-lg", "--shadow-card", "--shadow-card-hover",
  "--shadow-modal", "--shadow-nav", "--shadow-dropdown", "--shadow-hero", "--shadow-glow-teal",
  "--shadow-glow-focus", "--elev-raised", "--elev-sheet",
  "--grad-hero", "--grad-hero-glow", "--grad-tile-mint", "--grad-tile-teal", "--grad-tile-deep",
  "--grad-sidebar-dark", "--field-rest", "--hero-highlight",
  "--float-bg", "--float-border", "--float-shadow",
  "--brand-gradient-footer", "--brand-gradient-dark",
  "--skel-frame", "--skel-bone", "--skel-sheen", "--skel-bone-on-dark", "--rule-faint", "--rule-ghost",
  "--now-line", "--subject-1", "--color-info",
] as const;

/** Text tokens re-checked for contrast against the page after retinting. */
const TEXT_TOKENS = ["--color-primary", "--color-secondary", "--color-muted"] as const;

const RAMP_TOKENS = [
  "--color-brand-teal-50", "--color-brand-teal-100", "--color-brand-teal-200", "--color-brand-teal-300",
  "--color-brand-teal-400", "--color-brand-teal-500", "--color-brand-teal-600", "--color-brand-teal-700",
  "--color-brand-teal-800", "--color-brand-teal-900", "--color-brand-teal-950",
] as const;

const RAMP_SLOTS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

type Tokens = Record<string, string>;

interface Defaults {
  light: Tokens;
  dark: Tokens;
}

/** Reads the shipped value of each token in both themes (the override is removed first). */
function readDefaults(names: readonly string[]): Defaults {
  const root = document.documentElement;
  const previous = root.dataset.theme;
  const out: Defaults = { light: {}, dark: {} };
  try {
    for (const theme of ["light", "dark"] as const) {
      root.dataset.theme = theme;
      const cs = getComputedStyle(root);
      for (const name of names) {
        const v = cs.getPropertyValue(name).trim();
        if (v) out[theme][name] = v;
      }
    }
  } finally {
    if (previous === undefined) delete root.dataset.theme;
    else root.dataset.theme = previous;
  }
  return out;
}

const COLOUR_RE = /#[0-9a-fA-F]{3,6}\b|rgba?\([^)]*\)/g;

/** Rotates the hue of every colour found in a CSS value and scales its saturation. */
function retint(value: string, hueDelta: number, satScale: number): string {
  return value.replace(COLOUR_RE, (match) => {
    const c = parseColour(match);
    if (!c) return match;
    const hsl = rgbToHsl(c);
    if (hsl.s === 0) return match;
    return toCss(hslToRgb({ h: hsl.h + hueDelta, s: Math.min(1, hsl.s * satScale), l: hsl.l }, c.a));
  });
}

function block(selector: string, tokens: Tokens): string {
  const body = Object.entries(tokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");
  return `${selector} {\n${body}\n}`;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

function buildRamp(primary: Hsl): { stops: Record<number, string>; hsl: Record<number, Hsl>; ink: Hsl } {
  const base = clamp(primary.s, 0.3, 0.85);
  const ink = ensureContrast({ h: primary.h, s: base, l: Math.min(primary.l, 0.4) }, WHITE, 5, "darker");
  const inkIsPrimary = Math.abs(ink.l - primary.l) < 0.005;
  const at = (l: number, sf: number): Hsl => ({ h: primary.h, s: clamp(base * sf, 0, 1), l: clamp(l, 0.02, 0.985) });
  const hsl: Record<number, Hsl> = {
    50: at(0.965, 0.4),
    100: at(0.915, 0.55),
    200: at(0.85, 0.6),
    300: at(Math.max(0.64, ink.l + 0.3), 0.6),
    400: at(Math.max(0.52, ink.l + 0.22), 0.6),
    500: at(ink.l + 0.11, 0.78),
    // The tenant's own colour sits in the 600 slot (focus, gradients) when it is lighter than the ink.
    600: inkIsPrimary
      ? at(ink.l + 0.06, 0.95)
      : ensureContrast({ h: primary.h, s: clamp(primary.s, 0.3, 0.9), l: primary.l }, WHITE, 3, "darker"),
    700: ink,
    800: at(ink.l * 0.76, 0.9),
    900: at(0.095, 0.6),
    950: at(0.05, 0.6),
  };
  const stops: Record<number, string> = {};
  for (const [k, v] of Object.entries(hsl)) stops[+k] = hslCss(v.h, v.s, v.l);
  return { stops, hsl, ink };
}

function rgbOf(css: string | undefined, fallback: Rgba): Rgba {
  return (css ? parseColour(css) : null) ?? fallback;
}

/** Removes any tenant override. DrTutor's shipped palette shows again. */
export function clearTenantBrand(): void {
  document.getElementById(STYLE_ID)?.remove();
}

/** Points the browser chrome colour (mobile status bar) at the live page canvas. */
export function syncChromeColour(): void {
  const page = getComputedStyle(document.documentElement).getPropertyValue("--color-page").trim();
  if (!page) return;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", page);
}

export interface BrandColours {
  primary: string | null;
  accent: string | null;
}

/** Writes the tenant palette into the page. Safe to call again on any change. */
export function applyTenantBrand(colours: BrandColours): void {
  clearTenantBrand();

  const primaryHex = isHexColour(colours.primary) ? colours.primary.trim().toLowerCase() : null;
  const accentHex = isHexColour(colours.accent) ? colours.accent.trim().toLowerCase() : null;
  const usePrimary = primaryHex !== null && !DEFAULT_PRIMARIES.has(primaryHex);
  const useAccent = accentHex !== null && !DEFAULT_ACCENTS.has(accentHex);
  if (!usePrimary && !useAccent) {
    syncChromeColour();
    return;
  }

  const names = [...RETINT_TOKENS, ...RAMP_TOKENS, "--color-surface", "--color-page"];
  const defaults = readDefaults(names);

  const light: Tokens = {};
  const dark: Tokens = {};

  if (usePrimary) {
    const primary = rgbToHsl(parseColour(primaryHex) as Rgba);
    const shippedInk = rgbToHsl(rgbOf(defaults.light["--color-brand-teal-700"], { r: 17, g: 112, b: 107, a: 1 }));
    const hueDelta = primary.h - shippedInk.h;
    const satScale = clamp(primary.s / Math.max(shippedInk.s, 0.01), 0.15, 1.15);

    for (const theme of ["light", "dark"] as const) {
      const target = theme === "light" ? light : dark;
      for (const name of RETINT_TOKENS) {
        const v = defaults[theme][name];
        if (v) target[name] = retint(v, hueDelta, satScale);
      }
    }

    // Text must read at least as well as the shipped palette did.
    for (const theme of ["light", "dark"] as const) {
      const target = theme === "light" ? light : dark;
      const pageNow = rgbOf(target["--color-page"], WHITE);
      const pageShipped = rgbOf(defaults[theme]["--color-page"], pageNow);
      for (const name of TEXT_TOKENS) {
        const shipped = rgbOf(defaults[theme][name], pageShipped);
        const next = rgbOf(target[name], shipped);
        if (next.a < 1 || shipped.a < 1) continue;
        const need = Math.min(contrast(shipped, pageShipped), 7);
        if (contrast(next, pageNow) < need) {
          const fixed = ensureContrast(rgbToHsl(next), pageNow, need, theme === "light" ? "darker" : "lighter");
          target[name] = hslCss(fixed.h, fixed.s, fixed.l);
        }
      }
    }

    const { stops, hsl, ink } = buildRamp(primary);
    RAMP_TOKENS.forEach((name, i) => {
      light[name] = stops[RAMP_SLOTS[i]];
    });
    const h = (slot: number, l?: number, sf = 1) => hslCss(hsl[slot].h, hsl[slot].s * sf, l ?? hsl[slot].l);
    const rgb = (slot: number) => hslToRgb(hsl[slot]);

    light["--color-brand-navy"] = stops[900];
    light["--color-brand-navy-deep"] = stops[950];
    light["--color-accent"] = stops[700];
    light["--color-accent-soft"] = stops[100];
    light["--color-link"] = stops[700];
    light["--color-info"] = stops[700];
    light["--color-border-focus"] = stops[600];
    light["--now-line"] = stops[600];
    light["--subject-1"] = stops[600];
    light["--color-surface-teal"] = h(100, 0.94, 0.8);
    light["--color-info-soft"] = light["--color-surface-teal"];
    light["--brand-gradient"] = `linear-gradient(180deg, ${stops[600]} 0%, ${stops[700]} 100%)`;
    light["--field-underline"] = `linear-gradient(90deg, ${stops[700]} 0%, ${stops[500]} 55%, ${stops[300]} 100%)`;
    light["--shadow-glow-focus"] = `0 0 0 4px ${withAlpha(rgb(600), 0.2)}`;
    light["--shadow-glow-teal"] = `0 0 0 1px ${withAlpha(rgb(600), 0.35)}, 0 0 28px ${withAlpha(rgb(600), 0.26)}`;
    light["--light-source"] =
      `radial-gradient(900px 560px at 18% -12%, ${withAlpha(rgb(300), 0.2)}, transparent 64%), ` +
      `radial-gradient(640px 420px at 92% 0%, ${withAlpha(hslToRgb({ ...hsl[500], l: 0.6 }), 0.07)}, transparent 66%)`;
    light["--brand-gradient-footer"] = `linear-gradient(180deg, ${h(50, 0.955)} 0%, ${h(200, 0.88)} 100%)`;
    light["--brand-gradient-dark"] = `linear-gradient(180deg, ${stops[900]} 0%, ${stops[950]} 100%)`;
    light["--grad-hero"] = `linear-gradient(115deg, ${stops[950]} 0%, ${stops[900]} 34%, ${h(800, 0.15, 0.8)} 62%, ${stops[500]} 100%)`;
    light["--grad-hero-glow"] = `radial-gradient(520px 360px at 92% 18%, ${withAlpha(rgb(300), 0.2)}, transparent 70%)`;
    light["--grad-tile-mint"] = `linear-gradient(135deg, ${h(100, 0.945)} 0%, ${h(200, 0.78)} 100%)`;
    light["--grad-tile-teal"] = `linear-gradient(135deg, ${stops[600]} 0%, ${h(800, 0.17)} 100%)`;
    light["--grad-tile-deep"] = `linear-gradient(135deg, ${h(800, 0.2)} 0%, ${stops[950]} 100%)`;

    // Dark: the link shade is lifted until it reads on the dark canvas.
    const darkSurface = rgbOf(dark["--color-surface"], { r: 10, g: 22, b: 22, a: 1 });
    const link = ensureContrast({ ...hsl[300], l: Math.max(hsl[300].l, 0.6) }, darkSurface, 7, "lighter");
    dark["--color-link"] = hslCss(link.h, link.s, link.l);
    dark["--color-info"] = dark["--color-link"];
    dark["--now-line"] = hslCss(link.h, link.s, Math.min(0.7, link.l));
    dark["--subject-1"] = dark["--now-line"];
    const darkTop = ensureContrast({ ...hsl[600], l: ink.l + 0.04 }, WHITE, 4.2, "darker");
    dark["--brand-gradient"] = `linear-gradient(180deg, ${hslCss(darkTop.h, darkTop.s, darkTop.l)} 0%, ${stops[700]} 100%)`;
    dark["--field-underline"] = `linear-gradient(90deg, ${stops[500]} 0%, ${stops[300]} 100%)`;
    dark["--shadow-glow-focus"] = `0 0 0 4px ${withAlpha(rgb(300), 0.2)}`;
    dark["--light-source"] =
      `radial-gradient(900px 600px at 16% -12%, ${withAlpha(rgb(600), 0.2)}, transparent 64%), ` +
      `radial-gradient(640px 420px at 92% 0%, ${withAlpha(rgb(500), 0.08)}, transparent 66%)`;
    dark["--grad-tile-mint"] = `linear-gradient(135deg, ${h(800, 0.17)} 0%, ${h(900, 0.1)} 100%)`;
    dark["--grad-tile-teal"] = `linear-gradient(135deg, ${h(600, 0.26, 0.9)} 0%, ${h(900, 0.1)} 100%)`;
    dark["--grad-tile-deep"] = `linear-gradient(135deg, ${h(800, 0.15)} 0%, ${h(950, 0.04)} 100%)`;
  }

  if (useAccent) {
    const accent = rgbToHsl(parseColour(accentHex) as Rgba);
    const sand = (l: number, sf = 1): Hsl => ({ h: accent.h, s: clamp(accent.s * sf, 0, 1), l });
    const css = (c: Hsl) => hslCss(c.h, c.s, c.l);
    const soft = sand(0.93, 0.6);
    light["--color-brand-sand-100"] = css(sand(0.91, 0.6));
    light["--color-brand-sand-300"] = css(sand(Math.min(0.8, accent.l + 0.16), 0.8));
    light["--color-brand-sand-500"] = accentHex;
    light["--color-brand-gold"] = accentHex;
    light["--color-brand-sand-700"] = css(ensureContrast(sand(0.36), WHITE, 4.5, "darker"));
    light["--color-brand-sand-ink"] = css(sand(0.08, 0.7));
    light["--color-gold-soft"] = css(soft);
    light["--color-gold-ink"] = css(ensureContrast(sand(0.32, 0.85), hslToRgb(soft), 4.5, "darker"));
    light["--grad-tile-sand"] = `linear-gradient(135deg, ${css(sand(0.91, 0.6))} 0%, ${accentHex} 100%)`;
    const deepSoft = sand(0.1, 0.5);
    dark["--color-gold-soft"] = css(deepSoft);
    dark["--color-gold-ink"] = css(ensureContrast(sand(0.7, 0.7), hslToRgb(deepSoft), 7, "lighter"));
    dark["--grad-tile-sand"] = `linear-gradient(135deg, ${css(sand(0.26, 0.5))} 0%, ${css(sand(0.13, 0.5))} 100%)`;
  }

  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = [block(":root:root", light), block(':root:root[data-theme="dark"]', dark)].join("\n");
  document.head.appendChild(el);
  syncChromeColour();
}
